import { Box, Stack, Typography, Button } from '@mui/material';
import { useState } from 'react';
import { Language, useSetCandidateLanguageMutation } from '@generated/graphql';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ROUTES from 'constants/routes';
import { detectedLang } from 'config/i18nConfig';
import { CircularLoading, useToast } from '@myhrbox/myhrbox-frontend-core';
import useMe from 'store/user/useMe';
import Card from 'components/Card';
import LanguageItem from './components/LanguageItem';
import { Selectedlanguage } from './types';

const LANGUAGES = [Language.Ru, Language.En, Language.Kk, Language.Uz];

const LanguageSelect = () => {
  const { me, loading: meLoading } = useMe();
  const { showErrorToast, showSuccessToast } = useToast();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const i18nCurrentLanguage = i18n.language ? detectedLang(i18n.language) : Language.Ru;

  const [selectedLanguage, setSelectedLanguage] = useState<Selectedlanguage>(
    i18nCurrentLanguage || Language.Ru,
  );

  const onSelectLanguage = (language: Language) => {
    setSelectedLanguage(language);
    i18n.changeLanguage(language);
  };

  const [saveLanguage, { loading }] = useSetCandidateLanguageMutation();

  const onSaveLanguage = () => {
    saveLanguage({
      variables: {
        language: selectedLanguage,
      },
    })
      .then((res) => {
        const candidate = res.data?.setCandidateLanguage;
        if (candidate?.language === selectedLanguage) {
          navigate(ROUTES.CONFIRM_USER_DATA);
          showSuccessToast(t('successMessage'));
        } else {
          showErrorToast(t('errors.common'));
        }
      })
      .catch(() => {
        showErrorToast(t('errors.common'));
      });
  };

  if (meLoading || me?.language) return <CircularLoading />;

  return (
    <Card sx={{ flexGrow: { xs: 1, sm: 0 }, display: 'flex', flexDirection: 'column' }}>
      <Stack spacing={1.5} mb={3.5}>
        {LANGUAGES.map((language) => (
          <LanguageItem
            language={language}
            key={language}
            selectedLanguage={selectedLanguage}
            onSelectLanguage={() => onSelectLanguage(language)}
          />
        ))}
      </Stack>
      <Typography variant='h28' mb={{ xs: 1.5, sm: 2 }}>
        {t('languageSelect.greeting')}
      </Typography>
      <Typography
        variant='subtitle18rg'
        mb={3.5}
        sx={{
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          hyphens: 'auto',
        }}>
        {t('languageSelect.description')}
      </Typography>
      <Box flex={{ xs: 1, sm: 0 }} />
      <Button onClick={onSaveLanguage} fullWidth variant='contained' size='large' loading={loading}>
        {t('next')}
      </Button>
    </Card>
  );
};

export default LanguageSelect;
